import React, { useCallback, useEffect, useState, useMemo } from "react";
import TableContainer from "Common/TableContainer";
import { Loader } from "../../Common/Loader";
import { Pencil, Trash2, Plus, Search, FileText, Calendar } from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Common } from "helpers/common";
import moment from "moment";
import { AddHomework } from "./AddHomework";
import { getHomeworkList, deleteHomework } from "helpers/backend_helper";
import Select from "react-select";
import { useProgramStore } from "store/useProgramStore";
import { useClassStore } from "store/useClassStore";
import { useAuthStore } from "store/useAuthStore";

// Default options for dropdowns
const defaultProgramOption = { value: "", label: "All Programs" };
const defaultClassOption = { value: "", label: "All Classes" };

function HomeworkList() {

  // Zustand stores
  const { 
    programs,
    selectedProgram, 
    setSelectedProgram,
  } = useProgramStore();
  
  const { 
    classes,
    selectedClass, 
    setSelectedClass,
  } = useClassStore();

  const { user } = useAuthStore();

  const [homework, setHomework] = useState<any[]>([]);
  const [filteredHomework, setFilteredHomework] = useState<any[]>([]);
  const [eventData, setEventData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [show, setShow] = useState<boolean>(false);
  const [homeworkData, setHomeworkData] = useState<any>({});
  const uiHelper = new Common();

  // Use Zustand store data directly - convert to dropdown format
  const programOptions = useMemo(() => {
    const options = programs.map((program: any) => ({
      value: program.programId,
      label: program.programName
    }));
    return [defaultProgramOption, ...options];
  }, [programs]);

  const classOptions = useMemo(() => {
    const options = classes.map((classItem: any) => ({
      value: classItem.classId,
      label: classItem.className
    }));
    return [defaultClassOption, ...options];
  }, [classes]);

  // Convert selected values from Zustand store to dropdown format
  const selectedProgramOption = useMemo(() => {
    if (!selectedProgram) return defaultProgramOption;
    return {
      value: selectedProgram.programId,
      label: selectedProgram.programName
    };
  }, [selectedProgram]);

  const selectedClassOption = useMemo(() => {
    if (!selectedClass) return defaultClassOption;
    return {
      value: selectedClass.classId,
      label: selectedClass.className
    };
  }, [selectedClass]);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // Load homework when selectedProgram or selectedClass changes
  useEffect(() => {
    if (selectedProgram && selectedClass) {
      console.log("Loading homework with Program:", selectedProgram.programId, "Class:", selectedClass.classId);
      loadHomework(true);
    }
  }, [selectedProgram?.programId, selectedClass?.classId]);

  // Ensure default selections when stores are loaded
  useEffect(() => {
    if (programs.length > 0 && !selectedProgram) {
      console.log("Auto-selecting first program:", programs[0]);
      setSelectedProgram(programs[0]);
    }
  }, [programs.length]);

  useEffect(() => {
    if (classes.length > 0 && !selectedClass) {
      console.log("Auto-selecting first class:", classes[0]);
      setSelectedClass(classes[0]);
    }
  }, [classes.length]);

  // Apply search filter
  useEffect(() => {
    applyFilters();
  }, [homework, searchText]);

  async function loadHomework(reload: boolean) {
    try {
      setIsLoading(true);
      const programId = selectedProgram?.programId || 0;
      const classId = selectedClass?.classId || 0;
      
      console.log("API Call: getHomeworkList with Program ID:", programId, "Class ID:", classId);
      
      // Build query params
      const params: any = {};
      if (programId > 0) params.programId = programId;
      if (classId > 0) params.classId = classId;
      
      const response = await getHomeworkList(params);
      
      console.log("Homework API Response:", response);
      setHomework(response.data || []);
      setFilteredHomework(response.data || []);
    } catch (error) {
      console.error("Error loading homework:", error);
      setHomework([]);
      setFilteredHomework([]);
    } finally {
      setIsLoading(false);
    }
  }

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.homeworkId) {
      setEventData(cell);
    }
  };

  const handleDelete = async () => {
    if (eventData) {
      try {
        await deleteHomework(eventData);
        setDeleteModal(false);
        uiHelper.showSuccessMessage("Homework has been deleted successfully.");

        // remove the item and update list
        setHomework((prev) =>
          prev.filter((item) => item.homeworkId !== eventData.homeworkId)
        );
        setFilteredHomework((prev) =>
          prev.filter((item) => item.homeworkId !== eventData.homeworkId)
        );
      } catch (error) {
        console.error("Error deleting homework:", error);
        uiHelper.showErrorMessage("Error deleting homework");
      }
    }
  };

  const handleProgramChange = async (option: any) => {
    setIsFiltering(true);
    
    try {
      if (option && option.value && option.value !== "") {
        const program = programs.find(p => p.programId === option.value);
        if (program) {
          setSelectedProgram(program);
          console.log("Program selected:", program);
        }
      } else {
        setSelectedProgram(null);
        console.log("Program cleared");
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
    } finally {
      setIsFiltering(false);
    }
  };

  const handleClassChange = async (option: any) => {
    setIsFiltering(true);
    
    try {
      if (option && option.value && option.value !== "") {
        const classItem = classes.find(c => c.classId === option.value);
        if (classItem) {
          setSelectedClass(classItem);
          console.log("Class selected:", classItem);
        }
      } else {
        setSelectedClass(null);
        console.log("Class cleared");
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
    } finally {
      setIsFiltering(false);
    }
  };

  const filterSearchData = (e: any) => {
    const searchText = e.target.value;
    setSearchText(searchText);
  };

  const applyFilters = () => {
    let filteredData = [...homework];

    if (searchText.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          item.title.toLowerCase().includes(searchText.toLowerCase()) ||
          (item.description && item.description.toLowerCase().includes(searchText.toLowerCase())) ||
          (item.teacherName && item.teacherName.toLowerCase().includes(searchText.toLowerCase()))
      );
    }

    setFilteredHomework(filteredData);
  };

  const handleAddHomework = () => {
    setHomeworkData({});
    toggle();
  };

  const handleEdit = (data: any) => {
    setHomeworkData(data);
    toggle();
  };

  const onSave = (result: any) => {
    console.log("Save result:", result);
      // Usage in your state setters
  setHomework((prev) => upsertHomework(prev, result));
  setFilteredHomework((prev) => upsertHomework(prev, result));

    //loadHomework(true);
    toggle();
  };

  /// Helper function to handle update or add
  const upsertHomework = (prev: any[], result: any) => {
    const exists = prev.some((item) => item.homeworkId === result.homeworkId);
    return exists
      ? prev.map((item) =>
          item.homeworkId === result.homeworkId ? { ...item, ...result } : item
        )
      : [...prev, result];
  };
  



  const toggle = useCallback(() => {
    setShow(!show);
    if (show) {
      setHomeworkData(null);
    }
  }, [show]);

  const onFileDelete = (result: any) => {
    console.log("File delete result:", result);
    setHomeworkData({...homeworkData, attachmentUrl: ""});
    // TODO: update the homework data in the list
    setHomework((prev) =>
      prev.map((item) => item.homeworkId === result.homeworkId ? { ...item, attachmentUrl: "" } : item)
    );
    setFilteredHomework((prev) =>
      prev.map((item) => item.homeworkId === result.homeworkId ? { ...item, attachmentUrl: "" } : item)
    );
    //loadHomework(true);
  };

  const columns = [
    {
      header: "ID",
      accessorKey: "homeworkId",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span className="font-medium">#{row.original.homeworkId}</span>
      ),
    },
    {
      header: "Title",
      accessorKey: "title",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <FileText className="size-4 text-slate-500" />
          <span className="font-medium">{row.original.title}</span>
        </div>
      ),
    },
    {
      header: "Description",
      accessorKey: "description",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span className="text-sm text-slate-600 dark:text-zink-300 line-clamp-2">
          {row.original.description || '-'}
        </span>
      ),
    },
    {
      header: "Teacher",
      accessorKey: "teacherName",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.teacherName || '-'}</span>
      ),
    },
    {
      header: "Class",
      accessorKey: "className",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Assigned Date",
      accessorKey: "assignedDate",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1">
          <Calendar className="size-3 text-slate-500" />
          <span>{row.original.assignedDate ? moment(row.original.assignedDate).format("MMM DD, YYYY") : ''}</span>
        </div>
      ),
    },
    {
      header: "Due Date",
      accessorKey: "dueDate",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        const dueDate = row.original.dueDate ? moment(row.original.dueDate) : null;
        const isOverdue = dueDate && dueDate.isBefore(moment(), 'day');
        
        return (
          <div className="flex items-center gap-1">
            <Calendar className="size-3 text-slate-500" />
            <span className={isOverdue ? "text-red-600 font-medium" : ""}>
              {dueDate ? dueDate.format("MMM DD, YYYY") : ''}
            </span>
          </div>
        );
      },
    },
    {
      header: "Total Marks",
      accessorKey: "totalMarks",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {row.original.totalMarks ? row.original.totalMarks : ''}
        </span>
      ),
    },
    {
      header: "Status",
      accessorKey: "isActive",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.original.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
        }`}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        const homework = row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 text-blue-500 bg-blue-100 rounded hover:bg-blue-500 hover:text-white transition-colors duration-200"
              onClick={() => handleEdit(homework)}
            >
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 text-red-500 bg-red-100 rounded hover:bg-red-500 hover:text-white transition-colors duration-200"
              onClick={() => onClickDelete(homework)}
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
        <div className="xl:col-span-12">
          <div className="card" id="homeworkTable">

            <div className="card-body">
              {/* Filters Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 xl:grid-cols-12 mb-5">
                
                {/* Search */}
                <div className="lg:col-span-3">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for ..."
                      autoComplete="off"
                      value={searchText}
                      onChange={filterSearchData}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>

                {/* Program Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Program"
                    name="program"
                    options={programOptions}
                    value={selectedProgramOption}
                    onChange={handleProgramChange}
                    isLoading={programs.length === 0 || isFiltering}
                    isDisabled={isFiltering}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Class Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Class"
                    name="class"
                    options={classOptions}
                    value={selectedClassOption}
                    onChange={handleClassChange}
                    isLoading={classes.length === 0 || isFiltering}
                    isDisabled={isFiltering}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Add Homework Button */}
                <div className="lg:col-span-3">
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 w-full"
                    onClick={handleAddHomework}
                  >
                    <Plus className="inline-block size-4" />{" "}
                    <span className="align-middle">Add Homework</span>
                  </button>
                </div>
              </div>
            </div>


            {isLoading ? (
              <Loader />
            ) : (
              <div className="relative">
                {isFiltering && (
                  <div className="absolute inset-0 bg-white/50 dark:bg-zink-800/50 flex items-center justify-center z-10 rounded-md">
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-zink-300">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-custom-500 border-t-transparent"></div>
                      Filtering...
                    </div>
                  </div>
                )}
                <TableContainer
                  columns={columns}
                  data={filteredHomework}
                  isPagination={true}
                  customPageSize={15}
                  divclassName="overflow-x-auto"
                  tableclassName="w-full whitespace-nowrap"
                  theadclassName="ltr:text-left rtl:text-right bg-custom-200 dark:bg-zink-600"
                  thclassName="px-2.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                  tdclassName="px-2.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                  trclassName="even:bg-custom-50  dark:odd:bg-zink-700 dark:even:bg-zink-600"
                  PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
                />
              </div>
            )}

          </div>
        </div>
      </div>


      {homeworkData ? (
        <AddHomework 
          show={show} 
          onHide={toggle} 
          data={homeworkData} 
          onSave={onSave}
          onFileDelete={onFileDelete}
        />) : (<></>)}

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
    </React.Fragment>
  );
}

export default HomeworkList;


